import React, { useState, useEffect } from 'react';
import ImageLoader from '../components/ImageLoader';
import { Carousel } from 'react-responsive-carousel';
import "react-responsive-carousel/lib/styles/carousel.min.css";

// Food Display Categories 
type FoodCategory = 'all' | 'traditional' | 'specialty' | 'stir-fry' | 'soup' | 'grilled' | 'other';

// Food Photo interface
interface FoodPhoto {
  id: number;
  title: string;
  description: string;
  category: FoodCategory;
  price?: string; // Optional price field from filename
  fileName: string; // Direct reference to filename
}

// Definieer foodPhotosData buiten de component als constante
const foodPhotosData: { title: string; category: FoodCategory, description: string, price?: string, exactFileName: string }[] = [
  // Index 28 - Deze bestandsnaam komt overeen met de eerste foto in de map
  { title: 'A Mok Fish', category: 'specialty', description: "Classic Khmer steamed fish curry in banana leaf.", price: "3$", exactFileName: 'A Mok Fish.jpeg' }, 
  // Index 29
  { title: 'Chicken hot dry', category: 'specialty', description: "Spicy, dry-fried chicken bursting with flavor.", price: "2.5$", exactFileName: 'Chicken hot dry.jpeg' }, 
  // Index 30
  { title: 'Fried beef hot', category: 'specialty', description: "Tender beef stir-fried with fiery spices.", price: "3$", exactFileName: 'Fried beef hot.jpeg' }, 
  // Index 31
  { title: 'Fried beef mix', category: 'specialty', description: "Flavorful mix of beef and vegetables, wok-fried.", price: "3$", exactFileName: 'Fried beef mix.jpeg' }, 
  // Index 32
  { title: 'Fried chicken ginger', category: 'stir-fry', description: "Stir-fried chicken infused with fresh ginger.", price: "2.5$", exactFileName: 'Fried chicken ginger.jpeg' }, 
  // Index 33
  { title: 'Fried chicken hot', category: 'specialty', description: "Succulent chicken stir-fried with a spicy kick.", price: "2.5$", exactFileName: 'Fried chicken hot.jpeg' }, 
  // Index 34
  { title: 'Fried Corn with Dried Shrimps', category: 'stir-fry', description: "Sweet corn kernels stir-fried with savory dried shrimps.", price: "2$", exactFileName: 'Fried Corn with Dried Shrimps.jpeg' }, 
  // Index 35
  { title: 'Fried eels hot', category: 'specialty', description: "A unique delicacy: eels stir-fried with hot spices.", price: "3$", exactFileName: 'Fried eels hot.jpeg' }, 
  // Index 36
  { title: 'Fried eels mix', category: 'specialty', description: "Eels stir-fried with a mix of vegetables and sauce.", price: "3$", exactFileName: 'Fried eels mix.jpeg' }, 
  // Index 37
  { title: 'Fried mixed vegetable', category: 'stir-fry', description: "A healthy and vibrant medley of fresh stir-fried vegetables.", price: "2.5$", exactFileName: 'Fried mixed vegetable.jpeg' }, 
  // Index 38
  { title: 'Fried noodle', category: 'stir-fry', description: "Classic stir-fried noodles, a comforting favorite.", price: "3$", exactFileName: 'Fried noodle.jpeg' }, 
  // Index 39
  { title: 'Fried rice with shrimps', category: 'stir-fry', description: "Fragrant fried rice with plump, juicy shrimps.", price: "3$", exactFileName: 'Fried rice with shrimps.jpeg' }, 
  // Index 40
  { title: 'Fried seafood pepper', category: 'stir-fry', description: "Mixed seafood stir-fried with aromatic Kampot pepper.", price: "3.5$", exactFileName: 'Fried seafood pepper.jpeg' }, 
  // Index 41
  { title: 'Fried squid and pepper', category: 'stir-fry', description: "Tender squid stir-fried with fresh, spicy pepper.", price: "3.5$", exactFileName: 'Fried squid and pepper.jpeg' }, 
  // Index 42
  { title: 'Fry beef sesame', category: 'stir-fry', description: "Beef stir-fried until crispy and coated with sesame seeds.", price: "3$", exactFileName: 'Fry beef sesame.jpeg' }, 
  // Index 43
  { title: 'Grilled meat', category: 'grilled', description: "Succulent skewers of marinated meat, grilled to perfection.", price: "3$", exactFileName: 'grilled meat.jpeg' },
  // Index 44
  { title: 'Lok Lak', category: 'specialty', description: "Signature Khmer dish: marinated beef cubes with lime-pepper dip.", price: "3$", exactFileName: 'Lok Lak.jpeg' }, 
  // Index 45
  { title: 'Red Curry', category: 'specialty', description: "Rich and aromatic red curry with coconut milk and spices.", price: "3.5$", exactFileName: 'red curry.jpeg' },
  // Index 46
  { title: 'Sour Soup Lemongrass', category: 'soup', description: "Aromatic and tangy soup infused with fresh lemongrass.", price: "3$", exactFileName: 'Sour Soup Lemongrass.jpeg' }, 
  // Index 47
  { title: 'Sour soup cambodia', category: 'soup', description: "Traditional Khmer sour soup with local herbs and vegetables.", price: "3$", exactFileName: 'Sour soup cambodia.jpeg' }, 
  // Index 48
  { title: 'Sour soup stir', category: 'soup', description: "A hearty stir-fried version of sour soup with mixed ingredients.", price: "3$", exactFileName: 'Sour soup stir.jpeg' }, 
  // Index 49
  { title: 'Sour soup vietnamese', category: 'soup', description: "Refreshing Vietnamese-style sour soup with pineapple and herbs.", price: "3$", exactFileName: 'Sour soup vietnamese.jpeg' }, 
  // Index 50
  { title: 'Stir fry meat with vegetable', category: 'stir-fry', description: "Your choice of meat stir-fried with crisp, fresh vegetables.", price: "3$", exactFileName: 'Stir fry meat with vegetable.jpeg' },
];

const Menu: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<FoodCategory>('all');
  const [foodPhotos, setFoodPhotos] = useState<FoodPhoto[]>([]);

  useEffect(() => {
    // We zorgen ervoor dat de map direct met bestandsnamen werkt
    const photos: FoodPhoto[] = foodPhotosData.map((data, i) => ({
      id: i + 1,
      title: data.title,
      description: data.description,
      category: data.category,
      price: data.price,
      fileName: data.exactFileName
    }));
    setFoodPhotos(photos);
  }, []); // Lege dependency array omdat foodPhotosData nu constant is

  // Filter food photos based on active category
  const filteredPhotos = activeCategory === 'all'
    ? foodPhotos
    : foodPhotos.filter(photo => photo.category === activeCategory);

  // Stel de speciale items samen voor de carousel, met A Mok Fish als eerste
  const specialCarouselItems = [...foodPhotos].sort((a, b) => {
    // Zet A Mok Fish eerst
    if (a.fileName === 'A Mok Fish.jpeg') return -1;
    if (b.fileName === 'A Mok Fish.jpeg') return 1;
    
    // Voeg dan Red Curry toe
    if (a.fileName === 'red curry.jpeg') return -1;
    if (b.fileName === 'red curry.jpeg') return 1;
    
    return a.id - b.id;
  }).slice(0, 7);

  return (
    <div className="min-h-screen pt-20 pb-16 bg-tuktuk-beige bg-opacity-20">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl md:text-5xl font-display font-bold text-center text-tuktuk-brown mb-4">
          Our Culinary Showcase
        </h1>
        <p className="text-center max-w-2xl mx-auto mb-8 text-lg">
          Feast your eyes on our authentic Cambodian cuisine. Each dish tells a story of tradition, creativity, and passion.
        </p>
        <p className="text-center max-w-2xl mx-auto mb-8 italic">
          All dishes are prepared with fresh ingredients from our own farm, combining ancient recipes with our chef's unique vision.
        </p>

        {/* Feature Carousel */}
        <div className="max-w-4xl mx-auto mb-16 shadow-lg rounded-lg overflow-hidden bg-gray-100">
          <Carousel 
            autoPlay 
            infiniteLoop 
            showThumbs={false} 
            interval={4000}
            showStatus={false}
            className="carousel-container"
          >
            {specialCarouselItems.map((photo) => (
              <div key={photo.id} className="relative h-[70vh] max-h-[600px]">
                <div className="bg-black bg-opacity-70 text-white text-xs py-1 px-3 absolute top-0 right-0 z-10">
                  {photo.title} • {photo.fileName}
                </div>
                <ImageLoader 
                  fileName={photo.fileName}
                  alt={photo.title} 
                  className="w-full h-full object-contain"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white p-4 md:p-6">
                  <h2 className="text-xl md:text-2xl font-display font-bold mb-1 md:mb-2">{photo.title} {photo.price && <span className="text-lg md:text-xl text-tuktuk-red font-sans">({photo.price})</span>}</h2>
                </div>
              </div>
            ))}
          </Carousel>
        </div>
        
        {/* Delivery Section */}
        <div className="text-center mb-12 bg-white p-6 rounded-lg shadow-md max-w-3xl mx-auto">
          <h2 className="text-2xl font-display font-bold text-tuktuk-brown mb-3">Craving Our Food at Home?</h2>
          <p className="text-gray-700 mb-4">You can now enjoy your Tuk Tuk Restaurant favorites delivered right to your doorstep! Find us on GrabFood and FoodPanda.</p>
          <div className="flex justify-center gap-4">
            <a href="https://food.grab.com/kh/en/restaurant/kong-bey-restaurant-%E1%9E%A2%E1%9E%B6%E1%9E%A0%E1%9E%B6%E1%9E%9A%E1%9E%8A%E1%9F%92%E1%9E%8B%E1%9E%B6%E1%9E%93-%E1%9E%80%E1%9E%84%E1%9F%8B%E1%9E%94%E1%9E%B8-delivery/10-C6NCNY5CBAWELX" target="_blank" rel="noopener noreferrer" className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-5 rounded-lg transition duration-300">Order on GrabFood</a>
            <a href="https://www.foodpanda.com.kh/restaurant/b151/sach-ko-ang-kong-bei-restaurant-siem-reap" target="_blank" rel="noopener noreferrer" className="bg-pink-500 hover:bg-pink-600 text-white font-bold py-2 px-5 rounded-lg transition duration-300">Order on FoodPanda</a>
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {/* Buttons adjusted for new categories */}
          <button
            className={`px-6 py-2 rounded-full font-medium transition-colors text-sm md:text-base ${
              activeCategory === 'all'
                ? 'bg-tuktuk-brown text-white'
                : 'bg-white text-tuktuk-brown hover:bg-tuktuk-brown hover:text-white'
            }`}
            onClick={() => setActiveCategory('all')}
          >
            All Dishes
          </button>
          <button
            className={`px-6 py-2 rounded-full font-medium transition-colors text-sm md:text-base ${
              activeCategory === 'traditional'
                ? 'bg-tuktuk-brown text-white'
                : 'bg-white text-tuktuk-brown hover:bg-tuktuk-brown hover:text-white'
            }`}
            onClick={() => setActiveCategory('traditional')}
          >
            Traditional
          </button>
          <button
            className={`px-6 py-2 rounded-full font-medium transition-colors text-sm md:text-base ${
              activeCategory === 'stir-fry'
                ? 'bg-tuktuk-brown text-white'
                : 'bg-white text-tuktuk-brown hover:bg-tuktuk-brown hover:text-white'
            }`}
            onClick={() => setActiveCategory('stir-fry')}
          >
            Stir-fry / Fried
          </button>
          <button
            className={`px-6 py-2 rounded-full font-medium transition-colors text-sm md:text-base ${
              activeCategory === 'soup'
                ? 'bg-tuktuk-brown text-white'
                : 'bg-white text-tuktuk-brown hover:bg-tuktuk-brown hover:text-white'
            }`}
            onClick={() => setActiveCategory('soup')}
          >
            Soups
          </button>
          <button
            className={`px-6 py-2 rounded-full font-medium transition-colors text-sm md:text-base ${
              activeCategory === 'grilled'
                ? 'bg-tuktuk-brown text-white'
                : 'bg-white text-tuktuk-brown hover:bg-tuktuk-brown hover:text-white'
            }`}
            onClick={() => setActiveCategory('grilled')}
          >
            Grilled
          </button>
           <button
            className={`px-6 py-2 rounded-full font-medium transition-colors text-sm md:text-base ${
              activeCategory === 'specialty'
                ? 'bg-tuktuk-brown text-white'
                : 'bg-white text-tuktuk-brown hover:bg-tuktuk-brown hover:text-white'
            }`}
            onClick={() => setActiveCategory('specialty')}
          >
            Specialties
          </button>
          {/* Consider adding 'Other' if needed */}
        </div>

        {/* Food Photo Grid - 4 columns on large screens */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-16">
          {filteredPhotos.map((photo) => (
            <div
              key={photo.id}
              className="bg-white rounded-lg overflow-hidden shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl group"
            >
              <div className="relative h-56 md:h-64 overflow-hidden bg-gray-100 food-grid-item"> 
                <ImageLoader 
                  fileName={photo.fileName}
                  alt={photo.title} 
                  className="w-full h-full object-contain"
                />
                <div className="absolute top-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1">
                  {photo.fileName.split('.')[0]}
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-base md:text-lg font-display font-bold text-tuktuk-brown mb-1">{photo.title} {photo.price && <span className="text-sm md:text-base text-tuktuk-red font-sans">({photo.price})</span>}</h3>
                <p className="text-gray-600 text-xs md:text-sm">{photo.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action for Reservations */}
        <div className="bg-tuktuk-brown text-white p-8 rounded-lg shadow-lg text-center mb-8">
          <h2 className="text-2xl font-display font-bold mb-4">
            Taste the Tradition, Book Your Table!
          </h2>
          <p className="mb-6">
            Visit us for an unforgettable dining experience by the riverside. For reservations, contact us via WhatsApp or book online.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a 
              href="https://wa.me/817044761574" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-8 rounded-lg transition duration-300 flex items-center justify-center"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              WhatsApp Reservation
            </a>
            <a 
              href="/reservations" 
              className="bg-tuktuk-red hover:bg-red-700 text-white font-bold py-3 px-8 rounded-lg transition duration-300"
            >
              Book a Table Online
            </a>
          </div>
        </div>

        {/* Chef's Note */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-8">
          <h3 className="text-xl font-display font-bold text-tuktuk-brown mb-4">Chef's Note</h3>
          <p>
            Our menu changes seasonally to incorporate the freshest ingredients. Each dish shown represents our culinary philosophy, though specific dishes may vary during your visit. Please inform us of any dietary restrictions when making your reservation.
          </p>
        </div>
        
        {/* Styling for carousel & grid images */}
        <style>{`
          .carousel-container .slide {
            background: #f3f4f6; /* bg-gray-100 */
          }
          .carousel-container .slide img,
          .food-grid-item .object-contain { /* Target the img within the grid item */
            max-height: 100%;
            object-position: center;
            object-fit: contain; /* Ensure full visibility */
          }
          .food-grid-item {
            background-color: #f3f4f6; /* bg-gray-100 */
          }
        `}</style>
      </div>
    </div>
  );
};

export default Menu; 